/**
 * Attachments on a task or an idea, with upload (FEAT-021, addendum §5).
 *
 * The size check happens **before** the request goes out: uploading 200 MB for
 * a minute only to be refused is the failure this exists to prevent. The
 * client-side number is a mirror of the server's (see lib/limits.ts), and the
 * server stays the authority — a 413 still surfaces as an error toast.
 *
 * Progress is real bytes, not an indeterminate spinner: the upload goes through
 * XHR precisely so the button can say how far along it is (§5, §7).
 */

import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Paperclip } from "lucide-react";

import { Attachments } from "@/api/resources";
import type { AttachmentOut, AttachmentParentType } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Cell, CellGroup } from "@/components/ui/Cell";
import { Section } from "@/components/ui/Section";
import { SkeletonBlock } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { formatFileSize } from "@/lib/format";
import { MAX_ATTACHMENT_BYTES, MAX_ATTACHMENT_MB } from "@/lib/limits";

export interface AttachmentsSectionProps {
  parentType: AttachmentParentType;
  parentId: string;
  /** §3 — inside the idea sheet the block runs without its own section title. */
  compact?: boolean;
}

export function AttachmentsSection({ parentType, parentId, compact }: AttachmentsSectionProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [percent, setPercent] = useState<number | null>(null);
  const key = ["attachments", parentType, parentId];

  const files = useQuery({
    queryKey: key,
    queryFn: () => Attachments.list(parentType, parentId),
    enabled: Boolean(parentId),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      Attachments.create(parentType, parentId, file, (p) => setPercent(p.percent)),
    onSuccess: () => {
      setPercent(null);
      qc.invalidateQueries({ queryKey: key });
      // The count lives on the idea row too, so the list behind us is stale now.
      qc.invalidateQueries({ queryKey: ["ideas"] });
      toast.success("Файл приложен");
    },
    onError: (err: unknown) => {
      setPercent(null);
      const message = err instanceof Error ? err.message : "Не удалось загрузить файл";
      toast.error(message);
    },
  });

  const pick = () => inputRef.current?.click();

  const onPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so picking the *same* file again still fires onChange.
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error(`Файл больше ${MAX_ATTACHMENT_MB} МБ — не отправляю`);
      return;
    }
    setPercent(0);
    uploadMutation.mutate(file);
  };

  const items = files.data ?? [];

  const addButton = (
    <Button
      variant="secondary"
      size="compact"
      onClick={pick}
      loading={uploadMutation.isPending}
      loadingText={percent == null ? "Загружаем…" : `Загружаем… ${percent}%`}
      icon={<Paperclip className="h-4 w-4" aria-hidden="true" />}
    >
      Добавить файл
    </Button>
  );

  const body = (
    <>
      {files.isLoading ? (
        <SkeletonBlock className="h-11" />
      ) : items.length > 0 ? (
        <CellGroup inset="icon">
          {items.map((a) => (
            <AttachmentRow key={a.id} attachment={a} />
          ))}
        </CellGroup>
      ) : (
        <p className="text-meta text-hint">Файлов пока нет.</p>
      )}

      <div className="flex items-center gap-2">
        {addButton}
        <span className="text-chip text-hint">до {MAX_ATTACHMENT_MB} МБ</span>
      </div>

      {/*
        The picker itself: a real <input type=file> kept out of the layout, so
        the platform sheet and keyboard activation both work. Hidden with
        `sr-only`, not `display:none` — the latter makes it unfocusable.
      */}
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        onChange={onPicked}
        aria-label="Выбрать файл для загрузки"
        tabIndex={-1}
      />

      {/* ui-ux-pro-max: async status has to be announced, not only drawn. */}
      <span aria-live="polite" className="sr-only">
        {uploadMutation.isPending && percent != null ? `Загружено ${percent} процентов` : ""}
      </span>
    </>
  );

  if (compact) return <div className="flex flex-col gap-2">{body}</div>;

  return (
    <Section
      title="Вложения"
      trailing={items.length > 0 ? <span className="text-section text-hint">{items.length}</span> : undefined}
    >
      {body}
    </Section>
  );
}

function AttachmentRow({ attachment }: { attachment: AttachmentOut }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const isImage = attachment.mime.startsWith("image/");

  const download = async () => {
    setBusy(true);
    try {
      await Attachments.download(attachment.id, attachment.original_name);
    } catch {
      toast.error("Не удалось скачать файл");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Cell
      icon={<FileText className="h-5 w-5" aria-hidden="true" />}
      title={attachment.original_name}
      subtitle={`${isImage ? "изображение" : attachment.mime} · ${formatFileSize(attachment.size)}`}
      trailing={
        <Button
          variant="ghost"
          size="icon"
          onClick={download}
          loading={busy}
          aria-label={`Скачать ${attachment.original_name}`}
        >
          {!busy && <Download className="h-[18px] w-[18px]" aria-hidden="true" />}
        </Button>
      }
    />
  );
}
